import React, { useState, useCallback, useMemo } from "react";
import { Layout, Modal, message, Input, Form } from "antd";
import { useTranslation } from 'react-i18next';
import TransactionTable from "./TransactionTable/TransactionTable";
import TransactionDetailsModal from "./TransactionModal/TransactionDetailsModal";
import AddTransactionModal from "./TransactionModal/AddTransactionModal";
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { reconcileTransaction, updateTransaction } from './TransactionsServices';
import { useTheme } from "../Shared/GlobalContext/ThemeContext";
import useSocket from "../../Hooks/useSocket";
import useSocketSubscription from "../../Hooks/useSocketSubscription";
import useSocketEvent from "../../Hooks/useSocketEvent";
import { Resource, Action } from "../../Constants/socket";

const { TextArea } = Input;

const Transactions = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const [visibleModal, setVisibleModal] = useState(false);
    const [actionToConfirm, setActionToConfirm] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
    const [showTransactionDetailsModal, setShowTransactionDetailsModal] = useState(false);
    const [selectedTransactionDetails, setSelectedTransactionDetails] = useState(null);
    const [refreshTable, setRefreshTable] = useState(false);
    const { handleError } = useErrorHandler();
    const [transactions, setTransactions] = useState([]);
    const [disablingReason, setDisablingReason] = useState('');
    const [form] = Form.useForm();
    
    // Socket connection
    const { socket, connectionState } = useSocket();
    const isConnected = connectionState?.status === 'connected';
    
    // Also subscribe to the general transaction resource for new transactions
    useSocketSubscription(Resource.TRANSACTION);
    
    // Memoize transaction IDs for socket subscription
    const transactionIds = useMemo(() => {
        return transactions.map(transaction => transaction.id).filter(Boolean);
    }, [transactions]);
    
    // Subscribe to transaction resource with specific IDs
    useSocketSubscription(Resource.TRANSACTION, transactionIds.length > 0 ? transactionIds : null);

    const reconcileTransactionCall = useCallback(async (id) => {
        try {
            await reconcileTransaction(id, { is_reconciled: true });
            message.success(t('transaction_reconciled_successfully'));
            setRefreshTable(prev => !prev);
        } catch (error) {
            handleError(error);
        }
    }, [handleError, setRefreshTable, t]);
    
    const handleAction = (action, record) => {
        if (action === 'view') {
            setSelectedTransactionDetails(record);
            setShowTransactionDetailsModal(true);
            return;
        }
        if (action === 'reconcile' && record.transaction_type === 'payment') {
            reconcileTransactionCall(record.id);
            return;
        }
        showConfirmationModal(action, record);
    };

    const showConfirmationModal = (action, record) => {
        setActionToConfirm(action);
        setSelectedRecord(record);
        setDisablingReason('');
        form.resetFields();
        setVisibleModal(true);
    };

    const handleActionConfirmation = useCallback(async () => {
        // For disable action, validate the form first
        if (actionToConfirm === 'disable') {
            try {
                await form.validateFields();
            } catch (error) {
                return; // Don't proceed if validation fails
            }
        }
        
        setVisibleModal(false);
        try {
            const { id } = selectedRecord;
            const is_disabled = actionToConfirm === 'disable';
            
            // Include disabling reason when disabling a transaction
            const payload = is_disabled 
                ? { is_disabled, disabling_reason: disablingReason } 
                : { is_disabled };
                
            await updateTransaction(id, payload);
            message.success(t(is_disabled ? 'transaction_disabled_successfully' : 'transaction_enabled_successfully'));
            setRefreshTable(prev => !prev);
        } catch (error) {
            handleError(error);
        } finally {
            setActionToConfirm(null);
            setSelectedRecord(null);
            setDisablingReason('');
        }
    }, [actionToConfirm, selectedRecord, handleError, setActionToConfirm, setSelectedRecord, setVisibleModal, t, disablingReason, form]);

    const handleTransactionAdded = () => {
        setRefreshTable(prev => !prev);
    };
    
    // Socket event handlers
    const handleTransactionCreated = useCallback((data) => {
        console.log('handleTransactionCreated', data);
        // Trigger refresh for created transactions
        setRefreshTable(prev => !prev);
    }, []);
    
    const handleTransactionUpdated = useCallback((data) => {
        // Update the specific transaction in state without reloading
        setTransactions(prevTransactions => 
            prevTransactions.map(transaction => 
                transaction.id === data.id ? { ...transaction, ...data } : transaction
            )
        );
    }, []);
    
    // Register socket event listeners
    useSocketEvent(Resource.TRANSACTION, Action.CREATE, handleTransactionCreated);
    useSocketEvent(Resource.TRANSACTION, Action.UPDATE, handleTransactionUpdated);
    
    // Callback to receive transactions data from the table
    const handleTransactionsLoaded = useCallback((data) => {
        setTransactions(data);
    }, []);

    const renderModalContent = () => {
        if (actionToConfirm === 'disable') {
            return (
                <Form form={form} layout="vertical">
                    <p>{t('are_you_sure_you_want_to_action_the_transaction', { action: actionToConfirm })}</p>
                    <Form.Item
                        name="disablingReason"
                        label={t('disabling_reason')}
                        rules={[
                            {
                                required: true,
                                message: t('please_provide_a_reason_for_disabling_this_transaction')
                            }
                        ]}
                    >
                        <TextArea 
                            value={disablingReason}
                            onChange={(e) => setDisablingReason(e.target.value)}
                            placeholder="disabling_reason"
                            rows={4}
                        />
                    </Form.Item>
                </Form>
            );
        }
        
        return <p>{t('are_you_sure_you_want_to_action_the_transaction', { action: actionToConfirm })}</p>;
    };

    return (
        <Layout theme={isDarkMode ? 'dark' : 'light'}>
            <TransactionTable
                onAction={handleAction}
                onShowAddTransactionModal={() => setShowAddTransactionModal(true)}
                refreshTable={refreshTable}
                onTransactionsLoaded={handleTransactionsLoaded}
                externalTransactions={transactions}
            />
            <Modal
                title={t('confirm_action', { action: actionToConfirm })}
                visible={visibleModal}
                onOk={handleActionConfirmation}
                onCancel={() => setVisibleModal(false)}
                okText={t('confirm')}
                cancelText={t('cancel')}
            >
                {renderModalContent()}
            </Modal>
            <TransactionDetailsModal
                visible={showTransactionDetailsModal}
                onCancel={() => setShowTransactionDetailsModal(false)}
                transactionDetails={selectedTransactionDetails}
            />
            {showAddTransactionModal && <AddTransactionModal
                onCancel={() => setShowAddTransactionModal(false)}
                onTransactionAdded={handleTransactionAdded}
            />}
        </Layout>
    );
};

export default Transactions;
